import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLanguages } from '@/hooks/use-languages';
import {
  TaskHeader,
  TaskNotice,
  TaskSessionComplete,
  TaskShell,
  TaskStepDots,
  kitLang,
  taskSteps,
} from '@/features/contribution-kit';
import { CollectionPlaceStep, useCollectionPlace } from '@/features/collection-place';
import {
  ValidationGateBanner,
  ValidationGateBlockedScreen,
  ValidationGateDialog,
} from '@/features/validation-gate';
import type { SpeakLanguageOption } from '@/features/speak-contribute/types';
import { extractErrorMessage } from '@/features/speak-contribute/utils';
import { speakContributeCopy } from '@/features/speak-contribute/speak-contribute-copy';
import { useSpeakRecorder } from '@/features/speak-contribute/hooks/useSpeakRecorder';
import { useSpeakSession } from '@/features/speak-contribute/hooks/useSpeakSession';
import { useSpeakPhraseEdits } from '@/features/speak-contribute/hooks/useSpeakPhraseEdits';
import { SpeakSetup } from '@/features/speak-contribute/components/SpeakSetup';
import { SpeakPhraseCard } from '@/features/speak-contribute/components/SpeakPhraseCard';
import { SpeakRecorderPanel } from '@/features/speak-contribute/components/SpeakRecorderPanel';
import { SpeakActionBar } from '@/features/speak-contribute/components/SpeakActionBar';
import { OfflineTaskNotice } from '@/features/offline/components/OfflineTaskNotice';

/** "Enregistrer": read phrases aloud, one take per phrase. */
export default function SpeakContribute() {
  const { t, language } = useApp();
  const { toast } = useToast();
  const { activeLanguages, isLoading: isLoadingLanguages } = useLanguages();
  const copy = speakContributeCopy[kitLang(language)];
  const collectionPlace = useCollectionPlace();

  const recorder = useSpeakRecorder({
    onStartError: (error) =>
      toast({ title: t.genericError, description: extractErrorMessage(error) ?? t.micAccessError, variant: 'destructive' }),
  });
  const session = useSpeakSession({ recording: recorder, place: collectionPlace.place });
  const edits = useSpeakPhraseEdits(session);

  const languages = activeLanguages as SpeakLanguageOption[];
  const languageMeta = languages.find((item) => item.code === session.selectedLanguage);

  // Same exemption as the submit handler: the backend lets community-profile collection through the gate.
  if (session.validationGate.isBlocked && !session.collectorContext?.communityProfileId) {
    return <ValidationGateBlockedScreen />;
  }

  if (!session.sessionStarted) {
    return (
      <SpeakSetup
        t={t}
        copy={copy}
        selectedLanguage={session.selectedLanguage}
        recordingDevice={session.recordingDevice}
        activeLanguages={languages}
        isLoadingLanguages={isLoadingLanguages}
        isStartingSession={session.isStartingSession}
        sessionResumeActive={session.sessionResumeActive}
        onLanguageChange={session.setSelectedLanguage}
        onRecordingDeviceChange={session.setRecordingDevice}
        onBack={session.goHome}
        onStart={session.startLinkedSession}
        placeStep={<CollectionPlaceStep state={collectionPlace} />}
        isPlaceReady={collectionPlace.place !== null}
      />
    );
  }

  if (session.sessionFinished) {
    return (
      <TaskSessionComplete
        title={t.sessionComplete}
        description={copy.doneDescription}
        stats={[{ label: copy.doneRecorded, value: session.uploadedCount, tone: session.uploadedCount > 0 ? 'success' : 'default' }]}
        continueLabel={copy.doneBack}
        onContinue={session.dismissSessionFinished}
        onHome={session.goHome}
      />
    );
  }

  const { sentences, currentSentence, wordItems, reRecordData } = session;
  const wordItem = wordItems[currentSentence];
  const isLastSentence = currentSentence === sentences.length - 1;
  const steps = taskSteps(sentences.length, currentSentence, (index) => (session.completedSentences[index] ? 'done' : null));

  return (
    <TaskShell
      header={
        <TaskHeader
          task="speak"
          index={currentSentence}
          total={sentences.length}
          positionLabel={t.sentenceXofY.replace('{current}', String(currentSentence + 1)).replace('{total}', String(sentences.length))}
          progress={session.sessionProgress}
          itemId={wordItem?.id}
          onBack={session.goHome}
          meta={
            <>
              {languageMeta ? <span>{languageMeta.displayName || languageMeta.name}</span> : null}
              {session.isReRecordMode ? (
                <span className="rounded-full border border-info/30 px-2 py-0.5 text-info">{t.reRecordMode}</span>
              ) : null}
            </>
          }
        />
      }
      notices={
        <>
          <OfflineTaskNotice />
          {session.isReRecordMode && reRecordData ? (
            <TaskNotice tone="info" icon="mic" title={t.reRecordPhrase}>
              <p>{t.reRecordPhraseDescription}</p>
              {reRecordData.rejectionReason ? (
                <p className="mt-1">
                  <strong className="font-medium text-foreground">{t.rejectionReason}:</strong> {reRecordData.rejectionReason}
                  {reRecordData.detailedReason ? <span className="block">{reRecordData.detailedReason}</span> : null}
                </p>
              ) : null}
            </TaskNotice>
          ) : null}
          <ValidationGateBanner />
        </>
      }
      actionBar={
        <SpeakActionBar
          t={t}
          copy={copy}
          isRecording={recorder.isRecording}
          isPaused={recorder.isPaused}
          isUploading={session.isUploading}
          alreadyDone={session.alreadyDone}
          hasRecording={recorder.recordedAudio !== null}
          isLastSentence={isLastSentence}
          canGoPrevious={currentSentence > 0 || session.page > 1}
          hasSentences={sentences.length > 0}
          shortcutsEnabled={!edits.isTyping}
          onToggleRecording={recorder.isRecording ? recorder.stopRecording : recorder.startRecording}
          onTogglePause={recorder.togglePause}
          onPrevious={session.previousSentence}
          onValidateNext={session.validateAndNext}
        />
      }
    >
      {session.blockedDialogGate && (
        <ValidationGateDialog
          open
          onOpenChange={(open) => !open && session.setBlockedDialogGate(null)}
          gate={session.blockedDialogGate}
        />
      )}

      <SpeakPhraseCard
        t={t}
        editLabel={copy.editPhrase}
        wordItem={wordItem}
        sentence={sentences[currentSentence]}
        hasSentences={sentences.length > 0}
        isModerator={edits.isModerator}
        selectedLanguage={session.selectedLanguage}
        isEditingPhrase={edits.isEditingPhrase}
        editedPhrase={edits.editedPhrase}
        showKeyboard={edits.showKeyboard}
        onEditedPhraseChange={edits.setEditedPhrase}
        onUpdatePhrase={edits.handleUpdatePhrase}
        onCancelPhraseEdit={edits.cancelPhraseEdit}
        onShowKeyboard={edits.showPhraseKeyboard}
        onStartPhraseEdit={edits.startPhraseEdit}
        translationActions={edits.translationActions}
        footer={<TaskStepDots steps={steps} labels={{ current: t.sessionInProgress, done: t.completed, rejected: t.waiting, todo: t.waiting }} />}
      >
        <SpeakRecorderPanel
          copy={copy}
          recordingSuccessful={t.recordingSuccessful}
          isRecording={recorder.isRecording}
          isPaused={recorder.isPaused}
          alreadyDone={session.alreadyDone}
          recordedAudio={recorder.recordedAudio}
          stream={recorder.stream}
        />
      </SpeakPhraseCard>
    </TaskShell>
  );
}
